// L3 — advanced Rich Media components (Checkpoint 7.6E): media playback
// intent, carousel commands and pace, form completeness. Deterministic and
// pure like every layer: it REPORTS, never repairs.
//
//   - media: authored intent the browser will not honour as written
//     (`withSound` autoplay starts muted — RUNTIME-CONTRACT §7.2) and media
//     that nothing can ever start (no autoplay, no controls, no rule);
//   - carousel: `goTo` needs a slide index that exists; `next`/`prev` take no
//     arguments; auto-advance faster than a comfortable reading pace is reported;
//   - forms: fields no element presents (a required one can never be filled)
//     and forms nothing submits.

import type { Action, Element } from "../schema/document.js";
import type { IssueSink } from "../issues.js";
import { CAROUSEL_MIN_COMFORTABLE_INTERVAL_MS } from "../registry.js";
import type { DocIndex } from "./context.js";

interface Located {
  readonly action: Action;
  readonly path: string;
}

/** Every action of every rule (inside `after` too) with its document path. */
export function allActions(index: DocIndex): Located[] {
  const out: Located[] = [];
  const walk = (actions: readonly Action[], base: string) => {
    actions.forEach((a, i) => {
      const path = `${base}[${String(i)}]`;
      out.push({ action: a, path });
      if (a.type === "after") walk(a.do, `${path}.do`);
    });
  };
  (index.doc.rules ?? []).forEach((r, i) => {
    walk(r.do, `$.rules[${String(i)}].do`);
  });
  return out;
}

const STARTERS = new Set(["media.play", "media.toggle", "media.restart"]);

export function validateComponents(index: DocIndex, sink: IssueSink): void {
  const actions = allActions(index);
  const started = new Set(
    actions.flatMap(({ action: a }) => (STARTERS.has(a.type) && "target" in a ? [a.target] : []))
  );

  for (const [id, entry] of index.elements) {
    const el: Element = entry.value;
    if (el.type === "video" || el.type === "audio") {
      const pb = el.playback;
      if (pb.autoplay === "withSound")
        sink.warn(
          "MEDIA_AUTOPLAY_DEGRADED",
          `${entry.path}.playback.autoplay`,
          "Autoplay with sound is degraded to muted autoplay by browsers; sound needs a user gesture.",
          { element: id }
        );
      const controls = pb.controls ?? "none";
      if (pb.autoplay === "none" && controls === "none" && !started.has(id))
        sink.warn(
          "MEDIA_NEVER_STARTS",
          `${entry.path}.playback`,
          `Media "${id}" never starts: no autoplay, no controls and no interaction plays it.`,
          { element: id }
        );
    }
    if (el.type === "component" && el.component === "ia.carousel@1") {
      const ms = el.props.autoAdvanceMs;
      if (typeof ms === "number" && ms > 0 && ms < CAROUSEL_MIN_COMFORTABLE_INTERVAL_MS)
        sink.warn(
          "CAROUSEL_AUTOPLAY_TOO_FAST",
          `${entry.path}.props.autoAdvanceMs`,
          `Auto-advance under ${String(CAROUSEL_MIN_COMFORTABLE_INTERVAL_MS)} ms is hard to read and to stop.`,
          { min: CAROUSEL_MIN_COMFORTABLE_INTERVAL_MS, actual: ms }
        );
    }
  }

  for (const { action: a, path } of actions) {
    if (a.type !== "component.command") continue;
    const el = index.elements.get(a.target)?.value;
    if (el?.type !== "component" || el.component !== "ia.carousel@1") continue;
    const slides = el.slots?.slides?.length ?? 0;
    if (a.command === "goTo") {
      const i = a.args?.index;
      if (i === undefined || i >= slides)
        sink.add(
          "COMMAND_ARGS_INVALID",
          `${path}.args.index`,
          `goTo needs the index of an existing slide (0..${String(Math.max(0, slides - 1))}).`,
          { slides }
        );
    } else if (a.args !== undefined)
      sink.add("COMMAND_ARGS_INVALID", `${path}.args`, `"${a.command}" takes no arguments.`, {});
  }
}

export function validateFormPresentation(index: DocIndex, sink: IssueSink): void {
  const presented = new Set<string>();
  for (const { value: el } of index.elements.values())
    if (el.type === "field") presented.add(`${el.form}\u0000${el.field}`);
  const submitted = new Set(allActions(index).flatMap(({ action: a }) => (a.type === "form.submit" ? [a.form] : [])));
  (index.doc.forms ?? []).forEach((form, fi) => {
    const fp = `$.forms[${String(fi)}]`;
    form.fields.forEach((field, i) => {
      if (!presented.has(`${form.id}\u0000${field.id}`))
        sink.warn(
          "FORM_FIELD_NOT_PRESENTED",
          `${fp}.fields[${String(i)}]`,
          `Field "${field.id}" of form "${form.id}" is not shown by any element${field.required ? "; the form can never be valid" : ""}.`,
          { form: form.id, field: field.id }
        );
    });
    if (!submitted.has(form.id))
      sink.warn("FORM_NEVER_SUBMITTED", fp, `Nothing submits form "${form.id}" (no form.submit action).`, {
        form: form.id,
      });
  });
}
